/**
 * Automated Executive Summary Generator
 * One-click weekly/monthly board-ready summary of all KPIs, wins, and concerns.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Download, Calendar, TrendingUp, TrendingDown, Trophy, AlertTriangle, DollarSign, Users, ArrowRight, CheckCircle2, Target, Printer } from 'lucide-react';
import { getIndustryTerms } from '@/config/industryTerminology';

// Neutral, terminology-adapted summary for every industry without a bespoke
// dataset — uses universal business KPIs (no SPLH / DT Wait / Food Cost), so a
// real client of any sector sees relevant numbers in their own language.
const generateGenericSummary = (period, t) => {
  const isWeekly = period === 'weekly';
  return {
    period: isWeekly ? 'Week of Apr 14-20, 2026' : 'April 2026',
    overallGrade: 'B+',
    gradeContext: isWeekly ? 'Up from B last week' : 'Consistent with March',
    headline: isWeekly
      ? `Solid week — ${t.revenue.toLowerCase()} up 7% and margins holding. Overdue invoices need attention to protect cash flow.`
      : `A steady month: ${t.revenue.toLowerCase()} grew 6.2% and gross margin improved. Key focus: tighten collections and review supplier pricing.`,
    kpis: [
      { label: t.revenue, value: isWeekly ? '$54,200' : '$218,400', change: isWeekly ? '+7.0%' : '+6.2%', direction: 'up', vs: isWeekly ? 'vs prev week' : 'vs March' },
      { label: 'Gross Margin', value: isWeekly ? '62.4%' : '61.8%', change: '+1.2%', direction: 'up', vs: 'vs prev period' },
      { label: 'Labour Cost', value: isWeekly ? '28.4%' : '29.0%', change: '-0.6%', direction: 'up', vs: 'target: 30%' },
      { label: 'Cash at Bank', value: isWeekly ? '$86,400' : '$86,400', change: '+$12,100', direction: 'up', vs: 'vs prev period' },
      { label: `${t.customer} Rating`, value: '4.6/5', change: '+0.1', direction: 'up', vs: 'vs prev period' },
      { label: 'Overdue Invoices', value: isWeekly ? '$14,200' : '$14,200', change: '-$3,100', direction: 'up', vs: 'vs prev period' },
    ],
    wins: [
      `${t.revenue} up 7% on the back of stronger ${t.customer.toLowerCase()} retention`,
      'Gross margin improved as supplier costs were renegotiated',
      `Average ${t.revenue.toLowerCase()} per ${t.customerLower} up after add-on/upsell prompts were introduced`,
    ],
    concerns: [
      `$14,200 in overdue invoices — collections slipping beyond 30 days`,
      isWeekly ? 'Two large supplier payments fall in the same week' : 'Input costs still trending up vs budget',
    ],
    actions: [
      { action: 'Automate overdue-invoice reminders at 7/14/30 days', owner: 'Finance', deadline: 'This week', impact: 'Est. +$8,000 cash recovered' },
      { action: `Introduce a consistent add-on/upsell prompt for every ${t.customerLower}`, owner: 'Operations', deadline: isWeekly ? 'Next week' : 'May 1', impact: `Est. +8-12% ${t.revenue.toLowerCase()} per ${t.customerLower}` },
      { action: 'Re-quote top 3 suppliers to protect margin', owner: 'Owner', deadline: isWeekly ? 'Next week' : 'May 7', impact: 'Est. 2-5% input cost saving' },
    ],
    locationRankings: [],
  };
};

const generateQSRSummary = (period) => {
  const isWeekly = period === 'weekly';
  return {
    period: isWeekly ? 'Week of Apr 14-20, 2026' : 'April 2026',
    overallGrade: 'B+',
    gradeContext: isWeekly ? 'Up from B last week' : 'Consistent with March',
    headline: isWeekly
      ? 'Strong week led by Southport\'s upsell improvements. Prospect continues to lag on SPLH. Network revenue up 7% vs last week.'
      : 'April was a solid month: 3 of 5 locations exceeded SPLH targets. Revenue grew 6.2% MoM. Key risk: Prospect needs attention on cross-training.',
    kpis: [
      { label: 'Revenue', value: isWeekly ? '$54,200' : '$218,400', change: isWeekly ? '+7.0%' : '+6.2%', direction: 'up', vs: isWeekly ? 'vs prev week' : 'vs March' },
      { label: 'Avg SPLH', value: isWeekly ? '$64.80' : '$63.90', change: isWeekly ? '+$2.40' : '+$1.80', direction: 'up', vs: 'network avg' },
      { label: 'Labour Cost', value: isWeekly ? '24.8%' : '25.1%', change: isWeekly ? '-0.4%' : '-0.3%', direction: 'up', vs: 'target: 25%' },
      { label: 'DT Wait', value: isWeekly ? '3.3 min' : '3.4 min', change: isWeekly ? '-0.2 min' : '-0.1 min', direction: 'up', vs: 'target: 3.0 min' },
      { label: 'Customer Rating', value: isWeekly ? '4.5/5' : '4.4/5', change: '+0.1', direction: 'up', vs: 'vs prev period' },
      { label: 'Food Cost', value: isWeekly ? '31.2%' : '31.5%', change: '+0.3%', direction: 'down', vs: 'target: 31%' },
    ],
    wins: [
      'Southport SPLH reached $74.30 — best in network, 14% above target',
      'Suggestive sell script lifted combo attachment from 41% to 52% at Southport',
      'Joondalup customer rating hit 4.7/5 — highest in 3 months',
      isWeekly ? 'Penrith DT wait dropped to 3.1 min with new pre-staging workflow' : 'Network-wide DT accuracy improved to 97.8%',
    ],
    concerns: [
      'Prospect SPLH at $51.20 — lowest in network, 21% below target',
      'Footscray food cost spiked to 32.8% due to chicken overprep waste',
      isWeekly ? 'Staff attendance at Prospect dropped to 88%' : '2 locations still above 25% labour cost target',
    ],
    actions: [
      { action: 'Cross-train 2 crew at Prospect on grill station', owner: 'Store Manager', deadline: 'This week', impact: 'Est. +$1,200/mo revenue recovery' },
      { action: 'Roll out suggestive sell script to Footscray & Prospect', owner: 'Area Manager', deadline: isWeekly ? 'Next week' : 'May 1', impact: 'Est. +$6,600/mo network revenue' },
      { action: 'Install DT pre-staging shelf at Footscray', owner: 'Ops Manager', deadline: isWeekly ? 'Next week' : 'May 7', impact: 'Est. -1.0 min DT wait time' },
    ],
    locationRankings: [
      { location: 'Southport, QLD', splh: '$74.30', revenue: isWeekly ? '$12,400' : '$49,800', grade: 'A' },
      { location: 'Penrith, NSW', splh: '$68.50', revenue: isWeekly ? '$12,800' : '$51,200', grade: 'A-' },
      { location: 'Joondalup, WA', splh: '$62.10', revenue: isWeekly ? '$9,400' : '$38,200', grade: 'B+' },
      { location: 'Footscray, VIC', splh: '$57.20', revenue: isWeekly ? '$8,200' : '$33,600', grade: 'B-' },
      { location: 'Prospect, SA', splh: '$51.20', revenue: isWeekly ? '$7,100' : '$28,800', grade: 'C+' },
    ],
  };
};

const generateTechSummary = (period) => {
  const isWeekly = period === 'weekly';
  return {
    period: isWeekly ? 'Week of Apr 14-20, 2026' : 'April 2026',
    overallGrade: 'A-',
    gradeContext: 'Strongest month this quarter',
    headline: isWeekly
      ? 'Trial conversion surged to 24%. 2 enterprise accounts flagged at churn risk ($8,400 MRR). Focus: proactive CS outreach.'
      : 'MRR grew to $126,400 (+$2,400). Trial conversion averaged 22%. Net revenue retention at 108%. Key risk: 2 enterprise accounts inactive.',
    kpis: [
      { label: 'MRR', value: isWeekly ? '$126,400' : '$126,400', change: '+$2,400', direction: 'up', vs: 'vs prev period' },
      { label: 'Churn', value: '3.2%', change: '-0.3%', direction: 'up', vs: 'target: 3.5%' },
      { label: 'NPS', value: '62', change: '+4', direction: 'up', vs: 'vs prev period' },
      { label: 'Trial Conv.', value: isWeekly ? '24%' : '22%', change: '+5%', direction: 'up', vs: 'vs prev period' },
      { label: 'CAC', value: '$340', change: '-$20', direction: 'up', vs: 'target: $400' },
      { label: 'ARR', value: '$1.52M', change: '+$28.8K', direction: 'up', vs: 'vs prev period' },
    ],
    wins: [
      'Trial-to-paid conversion hit 24% — highest this quarter',
      'New onboarding email sequence driving 3x higher click-through',
      'NPS improved to 62 from 58 last month',
    ],
    concerns: [
      '2 enterprise accounts ($8,400 MRR) inactive 14+ days',
      'API P95 latency approaching 2s SLA threshold during peak',
    ],
    actions: [
      { action: 'Proactive CS outreach to at-risk enterprise accounts', owner: 'CS Lead', deadline: 'This week', impact: 'Save $8,400 MRR' },
      { action: 'Increase DB connection pool to prevent API latency', owner: 'Engineering', deadline: isWeekly ? 'Next week' : 'May 5', impact: 'Maintain SLA compliance' },
    ],
    locationRankings: [],
  };
};

const gradeColors = {
  'A': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'A-': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'B+': 'bg-blue-100 text-blue-700 border-blue-200',
  'B': 'bg-blue-100 text-blue-700 border-blue-200',
  'B-': 'bg-amber-100 text-amber-700 border-amber-200',
  'C+': 'bg-orange-100 text-orange-700 border-orange-200',
  'C': 'bg-red-100 text-red-700 border-red-200',
};

export const ExecutiveSummaryGenerator = ({ industry = 'general' }) => {
  const [period, setPeriod] = useState('weekly');
  const [isExpanded, setIsExpanded] = useState(false);
  const t = getIndustryTerms(industry);

  // hospitality/qsr keep the rich multi-location QSR board summary; technology
  // keeps its SaaS summary; every other industry gets the neutral,
  // terminology-adapted summary (no SPLH / DT Wait / Food Cost).
  const summary = t.key === 'technology'
    ? generateTechSummary(period)
    : t.key === 'hospitality'
      ? generateQSRSummary(period)
      : generateGenericSummary(period, t);

  return (
    <Card className="glass-card overflow-hidden" data-testid="executive-summary-generator">
      <div className="h-1 bg-gradient-to-r from-slate-700 via-slate-500 to-slate-400" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            Executive Summary
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {['weekly', 'monthly'].map(p => (
                <Button
                  key={p}
                  variant="ghost"
                  size="sm"
                  onClick={() => setPeriod(p)}
                  className={`text-xs h-7 px-3 ${period === p ? 'bg-white shadow-sm' : ''}`}
                  data-testid={`summary-period-${p}`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold shrink-0 ${gradeColors[summary.overallGrade] || 'bg-slate-100 text-slate-700'}`}>
              {summary.overallGrade}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px]">
                  <Calendar className="w-3 h-3 mr-1" />{summary.period}
                </Badge>
                <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200">
                  {summary.gradeContext}
                </Badge>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mt-2 font-medium">{summary.headline}</p>
            </div>
          </div>
        </div>

        {/* KPI Grid — compact multi-column, no wasted full-width rows */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {summary.kpis.map((kpi, i) => (
            <div key={i} className="bg-white/80 border border-slate-100 rounded-lg p-2.5 text-center" data-testid={`summary-kpi-${i}`}>
              <p className="text-[10px] text-slate-400 uppercase font-semibold">{kpi.label}</p>
              <p className="text-lg font-bold text-slate-800">{kpi.value}</p>
              <div className="flex items-center justify-center gap-1 mt-0.5">
                {kpi.direction === 'up' ? (
                  <TrendingUp className="w-3 h-3 text-emerald-500" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-red-500" />
                )}
                <span className={`text-[10px] font-semibold ${kpi.direction === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>{kpi.change}</span>
                <span className="text-[10px] text-slate-400">{kpi.vs}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Expandable Detail */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-slate-500"
          onClick={() => setIsExpanded(!isExpanded)}
          data-testid="summary-expand-btn"
        >
          {isExpanded ? 'Collapse' : 'View Full Summary'} — Wins, Concerns, Actions{isExpanded ? '' : '...'}
        </Button>

        {isExpanded && (
          <div className="space-y-4">
            {/* Wins */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5" />Wins
              </p>
              {summary.wins.map((win, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-slate-700 bg-emerald-50/50 rounded-lg p-2 border border-emerald-100">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                  {win}
                </div>
              ))}
            </div>

            {/* Concerns */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />Concerns
              </p>
              {summary.concerns.map((concern, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-slate-700 bg-red-50/50 rounded-lg p-2 border border-red-100">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                  {concern}
                </div>
              ))}
            </div>

            {/* Action Items */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1">
                <Target className="w-3.5 h-3.5" />Action Items
              </p>
              {summary.actions.map((action, i) => (
                <div key={i} className="bg-indigo-50/50 rounded-lg p-2.5 border border-indigo-100 space-y-1">
                  <p className="text-xs font-medium text-slate-800">{action.action}</p>
                  <div className="flex items-center gap-3 text-[10px] text-slate-500">
                    <span><Users className="w-3 h-3 inline mr-0.5" />{action.owner}</span>
                    <span><Calendar className="w-3 h-3 inline mr-0.5" />{action.deadline}</span>
                    <span className="text-emerald-600 font-semibold">{action.impact}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Location Rankings (QSR only) */}
            {summary.locationRankings.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2">Location Rankings</p>
                <div className="space-y-1">
                  {summary.locationRankings.map((loc, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/60 rounded-lg p-2 border border-slate-100 text-xs">
                      <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">{i + 1}</span>
                      <span className="font-medium flex-1">{loc.location}</span>
                      <span className="text-slate-500">SPLH: {loc.splh}</span>
                      <span className="text-slate-500">Rev: {loc.revenue}</span>
                      <Badge className={`text-[10px] ${gradeColors[loc.grade] || 'bg-slate-100'}`}>{loc.grade}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExecutiveSummaryGenerator;
